import { inject, Injectable } from '@angular/core';
import { AuthClaims } from '@pcp/core/interfaces';
import { DateTime } from 'luxon';
import { PCP_LOGGER } from '@pcp/logging';

/**
 * Above this, a clock difference is worth recording; below it, it is ordinary network and
 * processing latency between the server stamping `iat` and the client reading it.
 *
 * **One constant for one question.** Until 2026-09-29 the service asked it twice with different
 * answers: `getSkewInfo().hasSkew` used 5s while the telemetry event needed 60s, so skews between
 * the two were reported as skewed by the API and recorded nowhere — no event, no log — even though
 * 30s is already enough to break JWT validation. That dead band is why this is a constant.
 */
const SIGNIFICANT_SKEW_MS = 5_000;

/**
 * Service for detecting and compensating clock skew between client and server.
 *
 * Uses JWT 'iat' (issued at) claims from fresh tokens to detect time differences
 * between client and server clocks. Provides adjusted time calculations for
 * accurate JWT validation regardless of client clock accuracy.
 *
 * Key principles:
 * - Only detects skew from fresh tokens (not restored from storage)
 * - Fresh token 'iat' represents server's current time when token was created
 * - Skew = server time (iat) - client time (when received)
 * - Positive skew = client clock behind server
 * - Negative skew = client clock ahead of server
 */
@Injectable({
    providedIn: 'root'
})
export class TimeSkewService {

    private detectedSkewMs: number = 0;
    private lastDetectionTime?: DateTime;
    private skewDetectionCount: number = 0;

    private readonly log = inject(PCP_LOGGER).createScoped('auth');

    /**
     * Detects clock skew from a fresh token received from the server.
     *
     * This should ONLY be called when receiving tokens directly from server
     * (login, token refresh) - NOT when restoring tokens from storage.
     *
     * @param claims Decoded JWT claims containing 'iat' (issued at)
     * @param receivedAt When the token was received (defaults to now)
     */
    detectSkewFromFreshToken(claims: AuthClaims, receivedAt: DateTime = DateTime.now()): void {
        if (!claims.iat) {
            this.log.warn('Cannot detect skew - token missing iat claim');
            return;
        }

        const diff = claims.iat.diff(receivedAt);

        // Calculate skew: server time - local time
        const newSkewMs = diff.toMillis();

        // Update detection state
        this.detectedSkewMs = newSkewMs;
        this.lastDetectionTime = receivedAt;
        this.skewDetectionCount++;

        // Record every skew this service itself calls significant — the same threshold `hasSkew`
        // uses, so the API and the telemetry can never disagree about whether a clock is wrong.
        if (Math.abs(newSkewMs) > SIGNIFICANT_SKEW_MS) {
            const skewDirection = newSkewMs > 0 ? 'behind' : 'ahead';
            const skewMinutes = Math.abs(newSkewMs / 60000).toFixed(1);

            this.log.trackEvent('ClockSkewDetected', {
                iat: claims.iat.toString(),
                diff: diff.toHuman(),
                newSkewMs,
                skewMinutes,
                skewDirection,
                detectionCount: this.skewDetectionCount
            });

        } else {
            this.log.debug('No significant clock skew detected', { newSkewMs });
        }
    }

    /**
     * Gets the detected clock skew in seconds for JWT validation.
     * Used with JwtHelperService.isTokenExpired(token, offsetSeconds).
     *
     * @returns Offset in seconds to apply to JWT validation
     */
    getOffsetSeconds(): number {
        return this.detectedSkewMs / 1000;
    }

    /**
     * Gets the detected clock skew in milliseconds.
     *
     * @returns Skew in milliseconds (positive = client behind, negative = client ahead)
     */
    getSkewMs(): number {
        return this.detectedSkewMs;
    }

    /**
     * Gets the current time adjusted for detected clock skew.
     * This represents what the server's current time should be.
     *
     * @returns Server-adjusted current time as DateTime
     */
    getAdjustedNow(): DateTime {
        return DateTime.fromMillis(Date.now() + this.detectedSkewMs);
    }

    /**
     * Checks if clock skew has been detected from a fresh token.
     *
     * @returns True if skew has been detected, false if using default (0)
     */
    hasDetectedSkew(): boolean {
        return this.skewDetectionCount > 0;
    }

    /**
     * Gets information about the last skew detection for debugging.
     *
     * @returns Detection metadata
     */
    getSkewInfo(): {
        skewMs: number;
        skewSeconds: number;
        lastDetection?: DateTime;
        detectionCount: number;
        hasSkew: boolean;
    } {
        return {
            skewMs: this.detectedSkewMs,
            skewSeconds: this.detectedSkewMs / 1000,
            lastDetection: this.lastDetectionTime,
            detectionCount: this.skewDetectionCount,
            hasSkew: this.hasDetectedSkew() && Math.abs(this.detectedSkewMs) > SIGNIFICANT_SKEW_MS
        };
    }

    /**
     * Resets the detected skew to zero. Primarily for testing.
     */
    resetSkew(): void {
        this.detectedSkewMs = 0;
        this.lastDetectionTime = undefined;
        this.skewDetectionCount = 0;
    }
}
